import { useMemo } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { PaperFrameStyled } from '@/components/common/paperFrameStyled';
import { useLocalization } from '@/hooks/useLocalization';
import { useSelector } from 'react-redux';
import { WARootState } from '@/store/store';
import { useGetConfidenceMeterQuery } from '@/store/mgpApi';
import { StandardWidgetProps } from '@/models/WidgetModels';
import { ContentContainer } from '../contentContainer';

export default function DashboardConfidenceMeter({
  onDataLoaded,
  hideTitle,
  isWidget,
  title,
  container = 'widget',
}: StandardWidgetProps) {
  const { t } = useLocalization();
  const widgetTitle = title ?? t('confidenceMeter.title', 'Confidence Meter');
  const userInfo = useSelector((s: WARootState) => s.appView.UserInfo);
  const isMgpEnabled = Boolean(
    userInfo?.Integrations &&
    (userInfo.Integrations as Record<string, unknown>)['MGP'],
  );

  const token = userInfo?.Token;
  const { data, isFetching, isError } = useGetConfidenceMeterQuery(
    { imageWidth: 300, imageHeight: 200 },
    { skip: !token },
  );
  const imageSrc = useMemo(
    () => (data?.Image ? `data:image/png;base64,${data.Image}` : null),
    [data],
  );

  if (!isFetching && (data || isError)) {
    onDataLoaded?.();
  }

  const handleOpenMoneyGuide = () => {
    try {
      const token = userInfo?.Token ?? '';
      const url =
        (import.meta.env.VITE_PLATFORM_URL || '') +
        '/SSO/MoneyGuidePro?isInvestor=true&token=' +
        encodeURIComponent(token);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      // no-op
    }
  };

  const containerHeight = isWidget ? 'fit-content' : '100%';

  return (
    <ContentContainer
      container={container}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: containerHeight,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          height: containerHeight,
        }}
      >
        {!hideTitle && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant='h5'>{widgetTitle}</Typography>
          </Box>
        )}

        <Box
          sx={{
            width: 300,
            height: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
          }}
        >
          {isFetching ? (
            <CircularProgress />
          ) : isError ? (
            <Typography color='error'>{t('errorLoadingData')}</Typography>
          ) : imageSrc ? (
            <img
              src={imageSrc}
              alt={t('confidenceMeter.alt', 'Confidence Meter graphic')}
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          ) : null}
        </Box>

        {isWidget && isMgpEnabled && (
          <Box>
            <Button
              variant='text'
              onClick={handleOpenMoneyGuide}
            >
              {t('confidenceMeter.seeDetails', 'See Details')}
            </Button>
          </Box>
        )}
      </Box>
    </ContentContainer>
  );
}
